import { CountryCode } from "libphonenumber-js";
import { useTranslation } from "react-i18next";
import { useAppSelector, RootState } from "../../../../store";
import { IconActionPhone } from "../../../../common/icons/IconActionPhone/IconActionPhone";
import { IconActionMessage } from "../../../../common/icons/IconActionMessage/IconActionMessage";
import { IconWhatsAppOutline } from "../../../../common/icons/IconWhatsAppOutline/IconWhatsAppOutline";
import { ContactLink } from "../../../../common/components/ContactLink/ContactLink";
import { WatchlistToggle } from "../WatchlistToggle/WatchlistToggle";
import defaultContent from "../../../../content/actionReports";
import { parsePhoneNumber } from "../../../../common/util/phoneNumber";
import "./QuickContactIcons.scss";
import LeafCircleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafCircleLoader/LeafCircleLoader";

type QuickContactIconsProps = {
  phoneNumber?: string;
  watchlist?: boolean;
  toggleWatchlist?: () => void | Promise<void>;
  isLoading?: boolean;
  isChina?: boolean;
  onPhoneTrack?: () => void;
  onMessageTrack?: () => void;
  whatsAppNumber?: string;
  whatsAppLoading?: boolean;
  onWhatsAppTrack?: () => void;
};

export const QuickContactIcons = ({
  phoneNumber,
  watchlist,
  toggleWatchlist,
  isLoading = false,
  isChina = false,
  onPhoneTrack,
  onMessageTrack,
  whatsAppNumber,
  whatsAppLoading,
  onWhatsAppTrack,
}: QuickContactIconsProps) => {
  const { t } = useTranslation(["actionReports"]);
  const { user } = useAppSelector((state: RootState) => state.boot);

  const showCallIcon = useAppSelector(
    (state: RootState) => state?.boot?.configuration?.actionReports?.showCallIcon ?? true,
  );
  const showTextIcon = useAppSelector(
    (state: RootState) => state?.boot?.configuration?.actionReports?.showTextIcon ?? true,
  );
  const hideWhatsApp = useAppSelector((state: RootState) => state.boot.configuration.hideWhatsApp);

  // Determine if we should show watchlist
  const showWatchlist = !isChina && toggleWatchlist !== undefined;

  // Determine if we should show phone/message icons
  const showPhoneIcons = phoneNumber && (showCallIcon || showTextIcon);

  // Show loading placeholders when enrichment is loading and phone is expected from enrichment
  // (phoneNumber is undefined = not yet available from enrichment; empty string = API confirmed no phone)
  const showPhoneLoaders = isLoading && phoneNumber === undefined && (showCallIcon || showTextIcon);

  // wa.me links require digits only — strip the leading "+" and any formatting characters.
  const whatsAppContactNumber = whatsAppNumber?.replace(/\D/g, "");
  const showWhatsAppIcon = !hideWhatsApp && whatsAppContactNumber !== undefined;
  const showWhatsAppLoader = (whatsAppLoading ?? isLoading) && !hideWhatsApp && whatsAppContactNumber === undefined;

  // Return null if nothing to show
  if (!showWatchlist && !showPhoneIcons && !showPhoneLoaders && !showWhatsAppIcon && !showWhatsAppLoader) {
    return null;
  }

  // Parse phone number if provided
  const { isValid: hasValidPhone, formattedNumber } = parsePhoneNumber(phoneNumber, user.isoCountryCode as CountryCode);

  return (
    <div className="QuickContactIcons">
      {showWatchlist && (
        <div className="QuickContactIcons__button QuickContactIcons__button--watchlist">
          <WatchlistToggle watchlist={!!watchlist} watchlistToggle={toggleWatchlist!} isLoading={isLoading} />
        </div>
      )}
      {showPhoneLoaders && (
        <>
          {showCallIcon && <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />}
          {showTextIcon && <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />}
        </>
      )}
      {hasValidPhone && showCallIcon && (
        <ContactLink
          method="call"
          contact={formattedNumber}
          role="button"
          aria-label={t("phoneAction", defaultContent["phoneAction"] as string)}
          href={`tel:${formattedNumber}`}
          target="_parent"
          className="QuickContactIcons__button"
          onClick={onPhoneTrack}
        >
          <IconActionPhone />
        </ContactLink>
      )}
      {hasValidPhone && showTextIcon && (
        <ContactLink
          method="text"
          contact={formattedNumber}
          role="button"
          aria-label={t("messageAction", defaultContent["messageAction"] as string)}
          href={`sms:${formattedNumber}`}
          target="_parent"
          className="QuickContactIcons__button"
          onClick={onMessageTrack}
        >
          <IconActionMessage />
        </ContactLink>
      )}
      {showWhatsAppLoader && <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />}
      {showWhatsAppIcon && (
        <ContactLink
          method="whatsApp"
          contact={`+${whatsAppContactNumber}`}
          role="button"
          aria-label={t("whatsAppAction", defaultContent["whatsAppAction"] as string)}
          href={`https://wa.me/${whatsAppContactNumber}`}
          target="_blank"
          rel="noreferrer"
          className="QuickContactIcons__button"
          onClick={onWhatsAppTrack}
        >
          <IconWhatsAppOutline />
        </ContactLink>
      )}
    </div>
  );
};
